import React, { useEffect, useRef, useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, FormControl, FormHelperText, InputLabel, MenuItem, Paper,
    Select, SelectChangeEvent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, Typography,
} from '@mui/material';
import { PosService } from '../../services/pos.service';
import { getStoresByInstitutionId } from '../../services/store.service';
import { useToast } from '../../context/ToastContext';
import { PosDayEndBucket, PosDayEndReport as PosDayEndReportData } from '../../interfaces/pos.interface';
import { Store } from '../../interfaces/store.interface';
import { formatMoney } from '../../utils/currency';
import { toDayEndIso, toDayStartIso } from '../../utils/dateUtils';

// Same cap/comment as PosSaleList.tsx's STORE_PAGE_SIZE.
const STORE_PAGE_SIZE = 100;

// toDayStartIso/toDayEndIso now live in utils/dateUtils.ts (final-review F5 -- this was a
// byte-identical duplicate of PosSaleList.tsx's own copy). The X-Timezone header this request
// carries (PosService.getDayEndReport, tzHeaders()) does NOT window this day boundary on the
// server (final-review F3) -- it affects only the report's byHour buckets -- so this client-side
// conversion is what actually fixes the day window to the browser's zone, not a redundant
// belt-and-braces step.

// F4 (review round 1) + F4 test fix (review round 3): toISOString() is UTC, so a UTC+6 tenant
// running this at 00:30-05:59 local got the PREVIOUS local day as the default -- exactly the
// wrong-day failure mode this screen exists to avoid. Round 1 fixed this with local Date getters
// (getFullYear/getMonth/getDate), which read the OS/process zone -- correct in a real browser,
// but process.env.TZ reassignment is a no-op in this Jest/jsdom setup, so no test could actually
// PIN a zone to prove the fix; round 1's test only ever exercised whatever zone the runner's own
// OS happened to start in, and silently passed here only because this machine's OS zone is
// Asia/Dhaka. `Intl.DateTimeFormat`'s `timeZone` option, unlike a `Date` getter, is an explicit
// argument that overrides the OS/process zone regardless of environment -- `now` and `timeZone`
// default to the real clock and the browser's own zone in production, so callers are unaffected,
// but a test can now inject BOTH explicitly and get a result that is genuinely independent of
// the machine it runs on.
export const todayIso = (
    now: Date = new Date(),
    timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
): string => new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(now);

const BucketTable: React.FC<{ title: string; rows: PosDayEndBucket[] }> = ({ title, rows }) => (
    <TableContainer component={Paper} sx={{ flex: '1 1 320px', minWidth: 280 }} data-testid={`bucket-${title}`}>
        <Box sx={{ px: 2, pt: 1.5 }}>
            <Typography variant="subtitle2">{title}</Typography>
        </Box>
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Transactions</TableCell>
                    <TableCell align="right">Gross</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.length === 0 && (
                    <TableRow>
                        <TableCell colSpan={3}>
                            <Typography color="text.secondary" variant="body2">No data</Typography>
                        </TableCell>
                    </TableRow>
                )}
                {rows.map(row => (
                    <TableRow key={row.key}>
                        <TableCell>{row.label ?? row.key}</TableCell>
                        <TableCell align="right">{row.transactionCount}</TableCell>
                        <TableCell align="right">{formatMoney(row.grossSales)}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </TableContainer>
);

const HeadlineFigure: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <Paper sx={{ p: 2, flex: '1 1 160px', minWidth: 150 }} data-testid={`headline-${label}`}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h6">{value}</Typography>
    </Paper>
);

const PosDayEndReport: React.FC = () => {
    const toast = useToast();

    const [stores, setStores] = useState<Store[]>([]);
    const [storeListTruncated, setStoreListTruncated] = useState(false);
    const [storeId, setStoreId] = useState('');
    const [fromDate, setFromDate] = useState(todayIso());
    const [toDate, setToDate] = useState(todayIso());

    const [report, setReport] = useState<PosDayEndReportData | null>(null);
    const [loading, setLoading] = useState(true);
    // F8 (review round 2): a failed load used to leave `report` null forever -- a toast, then a
    // blank body with no way back, the same defect Task 5 shipped and fixed on PosSaleDetail.tsx
    // (its F3). Mirrors that screen's pattern: an error Alert with a Retry action, not a third
    // pattern of this client's own invention.
    const [loadError, setLoadError] = useState(false);

    // F7 (review round 2): three filters can each trigger their own fetch in quick succession --
    // a slow response to an EARLIER filter combination landing after a fast response to a LATER
    // one must never overwrite the screen with stale figures. An incrementing request id, checked
    // before every state write in `load`, makes only the most recently ISSUED request's response
    // (not necessarily the most recently RESOLVED one) ever reach the screen.
    const latestRequestId = useRef(0);

    useEffect(() => {
        loadStores();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeId, fromDate, toDate]);

    const loadStores = async () => {
        try {
            const response = await getStoresByInstitutionId(0, STORE_PAGE_SIZE);
            const content = response.content ?? [];
            setStores(content);
            setStoreListTruncated((response.totalElements ?? content.length) > content.length);
        } catch (err) {
            toast.showApiError(err, 'Failed to load stores');
        }
    };

    const load = async () => {
        const requestId = ++latestRequestId.current;
        try {
            setLoading(true);
            setLoadError(false);
            const data = await PosService.getDayEndReport({
                storeId: storeId || undefined,
                fromSoldAt: toDayStartIso(fromDate),
                toSoldAt: toDayEndIso(toDate),
            });
            // F7: a newer request was issued while this one was in flight -- its own resolution
            // (success or failure) is what the screen must show, not this stale one.
            if (requestId !== latestRequestId.current) return;
            setReport(data);
        } catch (err) {
            if (requestId !== latestRequestId.current) return;
            setLoadError(true);
            toast.showApiError(err, 'Failed to load the day-end report');
        } finally {
            if (requestId === latestRequestId.current) setLoading(false);
        }
    };

    const handleStoreChange = (event: SelectChangeEvent) => {
        setStoreId(event.target.value);
    };

    // F1/F2 (review round 1): this used to key on transactionCount alone, which is
    // COMPLETED-only. An all-voided day (transactionCount === 0, voidedAmount > 0) rendered
    // "No sales in this period." and hid BOTH the voided figure and an unbalanced cross-check --
    // exactly the day a manager most needs both, since both cross-check queries deliberately
    // count VOIDED lines too. A period is genuinely empty only when there were no completed
    // sales, no voids, AND nothing for the cross-check to say.
    const isEmpty = report !== null
        && report.transactionCount === 0
        && Number(report.voidedAmount) === 0
        && report.stockCrossCheck.qtySoldOnLines === 0
        && report.stockCrossCheck.balanced;

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>Day-End Report</Typography>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <FormControl sx={{ minWidth: 220 }}>
                    <InputLabel id="pos-day-end-store-label">Store</InputLabel>
                    <Select
                        labelId="pos-day-end-store-label"
                        label="Store"
                        value={storeId}
                        onChange={handleStoreChange}
                    >
                        <MenuItem value="">All stores</MenuItem>
                        {stores.map(store => (
                            <MenuItem key={store.id} value={store.id}>{store.storeName}</MenuItem>
                        ))}
                    </Select>
                    {storeListTruncated && (
                        <FormHelperText>Showing the first {STORE_PAGE_SIZE} stores.</FormHelperText>
                    )}
                </FormControl>

                <TextField
                    label="From date"
                    type="date"
                    value={fromDate}
                    onChange={e => setFromDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    // Filters the sold-at date -- the counter's own clock -- never received_at,
                    // exactly like PosSaleList.tsx's identical filters.
                    helperText="Filters by when the sale was sold, not when the server received it"
                />
                <TextField
                    label="To date"
                    type="date"
                    value={toDate}
                    onChange={e => setToDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                />
            </Box>

            {loadError ? (
                <Alert
                    severity="error"
                    action={(
                        <Button color="inherit" size="small" onClick={load}>
                            Retry
                        </Button>
                    )}
                >
                    Failed to load the day-end report.
                </Alert>
            ) : loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : !report ? null : (
                <>
                    {isEmpty && <Alert severity="info" sx={{ mb: 3 }}>No sales in this period.</Alert>}

                    {!isEmpty && !report.stockCrossCheck.balanced && (
                        <Alert severity="warning" sx={{ mb: 3 }}>
                            Stock movement does not match sales for this period: {report.stockCrossCheck.qtySoldOnLines}{' '}
                            units sold on sale lines vs {report.stockCrossCheck.qtyMovedInStockHistory} units moved in
                            stock history. This is a total-vs-total comparison, so it can still read as balanced when
                            two errors offset each other, and it does not identify which receipt is affected.
                        </Alert>
                    )}

                    {/* F6 (review round 1): the balanced case used to say nothing at all, so the
                        DTO's own caveat -- balanced=true proves the two totals agree, not that
                        every individual sale's stock applied -- was never surfaced. A quiet
                        confirmation still carries that caveat rather than silence reading as
                        "nothing was checked". */}
                    {!isEmpty && report.stockCrossCheck.balanced && (
                        <Alert severity="success" sx={{ mb: 3 }}>
                            Stock movement matches sales for this period ({report.stockCrossCheck.qtySoldOnLines} units).
                            This is a total-vs-total comparison, so it does not rule out two offsetting errors.
                        </Alert>
                    )}

                    {!isEmpty && (
                        <>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                                <HeadlineFigure label="Gross Sales" value={formatMoney(report.grossSales)} />
                                <HeadlineFigure label="Voided Amount" value={formatMoney(report.voidedAmount)} />
                                <HeadlineFigure label="Transactions" value={String(report.transactionCount)} />
                                <HeadlineFigure label="Average Basket" value={formatMoney(report.averageBasket)} />
                                <HeadlineFigure label="Tax" value={formatMoney(report.taxTotal)} />
                                <HeadlineFigure label="Discount" value={formatMoney(report.discountTotal)} />
                                <HeadlineFigure label="Rounding" value={formatMoney(report.roundingTotal)} />
                            </Box>

                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <BucketTable title="By Store" rows={report.byStore} />
                                <BucketTable title="By Terminal" rows={report.byTerminal} />
                                <BucketTable title="By Cashier" rows={report.byCashier} />
                                <BucketTable title="By Hour" rows={report.byHour} />
                            </Box>
                        </>
                    )}
                </>
            )}
        </Box>
    );
};

export default PosDayEndReport;
